import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Clock, CheckCircle, Lock } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import './CourseVideos.css';

// Helper function to get token from localStorage
const getToken = () => {
    return localStorage.getItem('access_token') || localStorage.getItem('token');
};

// Helper function to set token
const setToken = (token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('access_token', token);
};

// Helper function to prepare headers
const prepareHeaders = (options, token) => {
    const headers = {
        ...(options.headers),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    return headers;
};

// Helper function to handle session expiration
const handleSessionExpired = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
    window.location.href = '/Login';
    throw new Error('Session expired. Please log in again.');
};

// Helper function to retry request with refreshed token
const retryWithRefreshedToken = async (url, options, headers, refreshToken) => {
    const refreshRes = await fetch(`${window._env_.API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!refreshRes.ok) {
        return null;
    }

    const { access_token } = await refreshRes.json();
    setToken(access_token);
    headers.Authorization = `Bearer ${access_token}`;

    if (options.body instanceof FormData) {
        delete headers['Content-Type'];
    }

    return fetch(url, { ...options, headers });
};

// Helper function to handle 401 response
const handle401Response = async (url, options, headers) => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
        handleSessionExpired();
    }

    try {
        const response = await retryWithRefreshedToken(url, options, headers, refreshToken);
        return response && response.ok ? response : null;
    } catch (err) {
        console.error('Token refresh failed:', err);
        return null;
    }
};

// Helper function to handle error responses
const handleErrorResponse = async (response) => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! Status: ${response.status}`);
};

// Custom fetch function with token handling
const tokenFetch = async (url, options = {}) => {
    const token = getToken();
    const headers = prepareHeaders(options, token);

    let response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
        response = await handle401Response(url, options, headers);
        if (!response) {
            handleSessionExpired();
        }
    }

    if (!response.ok) {
        await handleErrorResponse(response);
    }

    return response.json();
};

// Sub-component for Video List
const VideoList = ({ materials, completedMaterials, currentContent, onSelectContent }) => (
    <div className="coursevideos-video-list">
        {materials.map((material) => (
            <div
                key={material.material_id}
                className={`coursevideos-video-item ${currentContent?.material_id === material.material_id ? 'active' : ''} ${completedMaterials[material.material_id] ? 'completed' : ''}`}
                onClick={() => onSelectContent(material)}
            >
                <span className="coursevideos-video-index">{material.order_index}</span>
                <div className="coursevideos-video-info">
                    <span className="coursevideos-video-title" data-tooltip={material.title}>{material.title}</span>
                    <span className="coursevideos-video-duration"><Clock size={14} /> {material.duration}</span>
                </div>
                {completedMaterials[material.material_id] ? <CheckCircle size={16} /> : <Play size={16} />}
            </div>
        ))}
    </div>
);

// Sub-component for Assessment Item
const AssessmentItem = ({ isCourseCompleted, isActive, onAssessmentClick }) => (
    <div className="coursevideos-assessment">
        <div
            className={`coursevideos-video-item ${isActive ? 'active' : ''} ${isCourseCompleted ? '' : 'locked'}`}
            onClick={isCourseCompleted ? onAssessmentClick : null}
        >
            <span className="coursevideos-video-index">T</span>
            <div className="coursevideos-video-info">
                <span className="coursevideos-video-title">Assessment</span>
            </div>
            {isCourseCompleted ? <Play size={16} /> : <Lock size={16} />}
        </div>
    </div>
);

// Sub-component for Player Details
const PlayerDetails = ({ currentContent, completedMaterials }) => (
    <div className="coursevideos-player-details">
        <h2>{currentContent.title}</h2>
        <div className="coursevideos-player-meta">
            <span><Clock size={14} /> {currentContent.duration}</span>
            <span>Video {currentContent.order_index}</span>
            {completedMaterials[currentContent.material_id] && (
                <span className="coursevideos-completed"><CheckCircle size={14} /> Completed</span>
            )}
        </div>
    </div>
);

// Sub-component for Test Link Section
const TestLinkSection = ({ testLinkRequested, isRequesting, onRequestTestLink, testLinkMessage }) => (
    <>
        <button
            className={`coursevideos-test-link ${testLinkRequested[1] || isRequesting ? 'locked' : ''}`}
            onClick={() => onRequestTestLink(false)}
            disabled={testLinkRequested[1] || isRequesting}
        >
            {testLinkRequested[1] ? (
                <><Lock size={16} /> Test Link Requested</>
            ) : isRequesting ? (
                'Requesting...'
            ) : (
                'Request Test Link'
            )}
        </button>
        {testLinkMessage && (
            <p className={`coursevideos-message ${testLinkMessage.includes('Failed') ? 'error' : 'success'}`}>
                {testLinkMessage}
            </p>
        )}
    </>
);

// Sub-component for Certificate Section
const CertificateSection = ({ isGeneratingCertificate, onGenerateCertificate, certificateMessage, showReattemptPrompt, reattemptChoice, onReattemptChange, onReattemptSubmit, isRequesting }) => (
    <>
        <button
            className={`coursevideos-test-link ${isGeneratingCertificate ? 'loading' : ''}`}
            onClick={onGenerateCertificate}
            disabled={isGeneratingCertificate}
        >
            {isGeneratingCertificate ? 'Generating...' : 'Generate Certificate'}
        </button>
        {certificateMessage && (
            <div className="coursevideos-messages">
                <p className={`coursevideos-message ${certificateMessage.includes('Failed') || certificateMessage.includes('attempt') ? 'error' : 'success'}`}>
                    {certificateMessage}
                </p>
            </div>
        )}
        {showReattemptPrompt && (
            <div className="coursevideos-reattempt-prompt">
                <p>Would you like to reattempt the test?</p>
                <label>
                    <input
                        type="radio"
                        name="reattempt"
                        value="yes"
                        checked={reattemptChoice === 'yes'}
                        onChange={onReattemptChange}
                    />
                    Yes
                </label>
                <label>
                    <input
                        type="radio"
                        name="reattempt"
                        value="no"
                        checked={reattemptChoice === 'no'}
                        onChange={onReattemptChange}
                    />
                    No
                </label>
                <button
                    className="coursevideos-submit-reattempt"
                    onClick={onReattemptSubmit}
                    disabled={!reattemptChoice || isRequesting}
                >
                    {isRequesting ? 'Submitting...' : 'Submit'}
                </button>
            </div>
        )}
    </>
);

// Sub-component for Assessment Content
const AssessmentContent = ({ testLinkRequested, isRequesting, onRequestTestLink, testLinkMessage, isGeneratingCertificate, onGenerateCertificate, certificateMessage, showReattemptPrompt, reattemptChoice, onReattemptChange, onReattemptSubmit }) => (
    <div className="coursevideos-assessment-content">
        <h2>Assessment</h2>
        <div className="coursevideos-assessment-details">
            <h3>Test Instructions</h3>
            <ol>
                <li>
                    <strong>Watch All Videos First</strong>
                    <p>Please attempt the test only after completing all the course videos and ensuring you are confident with all the topics. You will have a maximum of two attempts to clear the test.</p>
                </li>
                <li>
                    <strong>Passing Criteria</strong>
                    <p>You will receive a second attempt only if your first attempt score is below 80%. A minimum score of 80% is required to earn the certificate.</p>
                </li>
                <li>
                    <strong>Requesting the Test</strong>
                    <p>To take the test, click on the "Request Test Link" button below. A test link will be sent to your registered email address within 48 hours, through which you can take the test.</p>
                    <TestLinkSection
                        testLinkRequested={testLinkRequested}
                        isRequesting={isRequesting}
                        onRequestTestLink={onRequestTestLink}
                        testLinkMessage={testLinkMessage}
                    />
                </li>
                <br />
                <li>
                    <strong>Generating Certificate</strong>
                    <p>After completing the test, you can click on "Generate Certificate" to view your score and download your certificate.</p>
                    <CertificateSection
                        isGeneratingCertificate={isGeneratingCertificate}
                        onGenerateCertificate={onGenerateCertificate}
                        certificateMessage={certificateMessage}
                        showReattemptPrompt={showReattemptPrompt}
                        reattemptChoice={reattemptChoice}
                        onReattemptChange={onReattemptChange}
                        onReattemptSubmit={onReattemptSubmit}
                        isRequesting={isRequesting}
                    />
                </li>
            </ol>
        </div>
    </div>
);

// Sub-component for Player Section Content
const PlayerSectionContent = ({ currentContent, completedMaterials, testLinkRequested, isRequesting, onRequestTestLink, testLinkMessage, isGeneratingCertificate, onGenerateCertificate, certificateMessage, showReattemptPrompt, reattemptChoice, onReattemptChange, onReattemptSubmit }) => {
    if (!currentContent) {
        return (
            <div className="coursevideos-placeholder">
                <Play size={40} />
                <p>Select a video or assessment to start learning</p>
            </div>
        );
    }

    if (currentContent.type === 'assessment') {
        return (
            <AssessmentContent
                testLinkRequested={testLinkRequested}
                isRequesting={isRequesting}
                onRequestTestLink={onRequestTestLink}
                testLinkMessage={testLinkMessage}
                isGeneratingCertificate={isGeneratingCertificate}
                onGenerateCertificate={onGenerateCertificate}
                certificateMessage={certificateMessage}
                showReattemptPrompt={showReattemptPrompt}
                reattemptChoice={reattemptChoice}
                onReattemptChange={onReattemptChange}
                onReattemptSubmit={onReattemptSubmit}
            />
        );
    }

    return null;
};

function CourseVideos() {
    const location = useLocation();
    const navState = location.state;
    const courseId = navState?.courseId;

    const [materials, setMaterials] = useState([]);
    const [currentContent, setCurrentContent] = useState(null);
    const [completedMaterials, setCompletedMaterials] = useState({});
    const [userId, setUserId] = useState(null);
    const [courseName, setCourseName] = useState('Loading...');
    const [testLinkRequested, setTestLinkRequested] = useState({ 1: false, 2: false });
    const [isRequesting, setIsRequesting] = useState(false);
    const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
    const [testLinkMessage, setTestLinkMessage] = useState('');
    const [certificateMessage, setCertificateMessage] = useState('');
    const [showReattemptPrompt, setShowReattemptPrompt] = useState(false);
    const [reattemptChoice, setReattemptChoice] = useState(null);
    const [videoProgress, setVideoProgress] = useState({ total_videos: 0, completed_videos: 0 });
    const [loadingProgress, setLoadingProgress] = useState(true); // New loading state
    const playerRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const playerContainerRef = useRef(null);

    useEffect(() => {
        const fetchUser = async () => {
            const userEmail = localStorage.getItem('userEmail');
            if (userEmail) {
                try {
                    const data = await tokenFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
                    if (!Number.isInteger(data.user_id)) {
                        throw new TypeError('Invalid user_id received');
                    }
                    setUserId(data.user_id);
                } catch (error) {
                    console.error("Failed to fetch user info:", error);
                }
            } else {
                console.error("No userEmail found in localStorage");
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (!courseId) return;

        if (!Number.isInteger(courseId)) {
            console.error("Invalid courseId:", courseId);
            return;
        }

        const fetchMaterials = async () => {
            try {
                const data = await tokenFetch(`${window._env_.API_BASE_URL}/courses/${courseId}/materials`);
                setMaterials(data);
            } catch (error) {
                console.error("Failed to fetch materials:", error);
            }
        };

        const fetchCourseName = async () => {
            try {
                const data = await tokenFetch(`${window._env_.API_BASE_URL}/courses/${courseId}`);
                setCourseName(data.course_name || 'Course Videos');
            } catch (error) {
                console.error("Failed to fetch course name:", error);
            }
        };

        fetchMaterials();
        fetchCourseName();
    }, [courseId]);

    useEffect(() => {
        if (!courseId || !userId) return;

        const fetchCompletionStatus = async () => {
            try {
                const data = await tokenFetch(`${window._env_.API_BASE_URL}/users/${userId}/courses/${courseId}/materials/completion`);
                setCompletedMaterials(data);
                console.log("Completion status fetched:", data); // Debug log
            } catch (error) {
                console.error("Failed to fetch completion status:", error);
            }
        };

        const fetchVideoProgress = async () => {
            setLoadingProgress(true); // Start loading
            try {
                const data = await tokenFetch(`${window._env_.API_BASE_URL}/dashboard/progress/videos?user_id=${userId}&course_id=${courseId}`);
                console.log("Video progress fetched:", data); // Debug log
                setVideoProgress(data);
            } catch (error) {
                console.error("Failed to fetch video progress:", error);
                setVideoProgress({ total_videos: 0, completed_videos: 0 }); // Fallback
            } finally {
                setLoadingProgress(false); // End loading
            }
        };

        const checkTestLinkRequest = async (testNumber) => {
            try {
                const data = await tokenFetch(`${window._env_.API_BASE_URL}/wecp/check-test-link-request/?user_id=${userId}&course_id=${courseId}&test_number=${testNumber}`);
                setTestLinkRequested(prev => ({ ...prev, [testNumber]: data.requested }));
                if (data.requested) {
                    setTestLinkMessage(`Test link for attempt ${testNumber} has been requested. A test link will appear within 48 hours in your email.`);
                }
            } catch (error) {
                console.error(`Error checking test link request for test_number=${testNumber}:`, error);
            }
        };

        fetchCompletionStatus();
        fetchVideoProgress();
        checkTestLinkRequest(1);
        checkTestLinkRequest(2);
    }, [courseId, userId]);

    const trackProgress = async (materialId, watchedSeconds) => {
        if (!userId || !materialId || watchedSeconds == null) return;
        try {
            await tokenFetch(`${window._env_.API_BASE_URL}/users/track_progress`, {
                method: 'POST',
                body: JSON.stringify({
                    user_id: userId,
                    material_id: materialId,
                    watched_seconds: Math.round(watchedSeconds),
                }),
            });
        } catch (error) {
            console.error('Error tracking progress:', error);
        }
    };

    // const onPlayerStateChange = useCallback((event) => {
    //     if (event.data === window.YT?.PlayerState.PLAYING) {
    //         clearInterval(progressIntervalRef.current);
    //         progressIntervalRef.current = setInterval(() => {
    //             const watchedSeconds = playerRef.current?.getCurrentTime?.();
    //             if (watchedSeconds && currentContent?.material_id) {
    //                 trackProgress(currentContent.material_id, watchedSeconds);
    //             }
    //         }, 5000);
    //     } else {
    //         clearInterval(progressIntervalRef.current);
    //     }

    //     if (event.data === window.YT?.PlayerState.ENDED) {
    //         clearInterval(progressIntervalRef.current);
    //         if (currentContent?.material_id) {
    //             setCompletedMaterials(prev => ({ ...prev, [currentContent.material_id]: true }));
    //             const watchedSeconds = playerRef.current?.getDuration?.();
    //             if (watchedSeconds) {
    //                 trackProgress(currentContent.material_id, watchedSeconds);
    //             }
    //         }
    //     }
    // }, [currentContent, trackProgress]);
    const onPlayerStateChange = useCallback((event) => {
        if (event.data === window.YT?.PlayerState.PLAYING) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = setInterval(() => {
                const watchedSeconds = playerRef.current?.getCurrentTime?.();
                if (watchedSeconds && currentContent?.material_id) {
                    trackProgress(currentContent.material_id, watchedSeconds);
                }
            }, 5000);
        } else {
            clearInterval(progressIntervalRef.current);
        }

        if (event.data === window.YT?.PlayerState.ENDED) {
            clearInterval(progressIntervalRef.current);
            if (currentContent?.material_id) {
                setCompletedMaterials(prev => ({ ...prev, [currentContent.material_id]: true }));
                const watchedSeconds = playerRef.current?.getDuration?.();
                if (watchedSeconds) {
                    trackProgress(currentContent.material_id, watchedSeconds);
                }
                const currentIndex = materials.findIndex(m => m.material_id === currentContent.material_id);
                const nextIndex = currentIndex + 1;
                if (nextIndex < materials.length) {
                    setCurrentContent(materials[nextIndex]);
                } else if (currentIndex === -1 && materials.length > 0) {
                    setCurrentContent(materials[0]);
                }
            }
        }
    }, [currentContent, trackProgress, materials]);

    useEffect(() => {
        if (!currentContent || currentContent.type === 'assessment' || !currentContent.video_id || !playerContainerRef.current) {
            if (playerRef.current) {
                try {
                    playerRef.current.destroy();
                } catch (error) {
                    console.error('Error destroying player during cleanup:', error);
                }
                playerRef.current = null;
            }
            clearInterval(progressIntervalRef.current);
            return;
        }

        const loadPlayer = () => {
            if (!document.getElementById('youtube-player')) {
                console.error('YouTube player container not found');
                return;
            }

            if (playerRef.current) {
                try {
                    playerRef.current.destroy();
                } catch (error) {
                    console.error('Error destroying player:', error);
                }
                playerRef.current = null;
            }

            playerRef.current = new window.YT.Player('youtube-player', {
                videoId: currentContent.video_id,
                width: '100%',
                height: '100%',
                events: {
                    onStateChange: onPlayerStateChange,
                },
            });
        };

        const timeout = setTimeout(() => {
            if (!window.YT && !document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                const tag = document.createElement('script');
                tag.src = 'https://www.youtube.com/iframe_api';
                window.onYouTubeIframeAPIReady = loadPlayer;
                document.body.appendChild(tag);
            } else if (window.YT) {
                loadPlayer();
            }
        }, 100);

        return () => {
            clearTimeout(timeout);
            clearInterval(progressIntervalRef.current);
            if (playerRef.current) {
                try {
                    playerRef.current.destroy();
                } catch (error) {
                    console.error('Error destroying player during cleanup:', error);
                }
                playerRef.current = null;
            }
        };
    }, [currentContent, onPlayerStateChange]);

    const isCourseCompleted = () => {
        if (materials.length === 0) return false;
        return materials.every(material => completedMaterials[material.material_id]);
    };

    const handleAssessmentClick = () => {
        setCurrentContent({ type: 'assessment' });
        setTestLinkMessage('');
        setCertificateMessage('');
        setShowReattemptPrompt(false);
        setReattemptChoice(null);
    };

    const handleReattemptChange = (e) => {
        setReattemptChoice(e.target.value);
    };

    const validateUserAndCourse = () => {
        if (!userId || !courseId) {
            console.error('Missing userId or courseId:', { userId, courseId });
            return false;
        }
        if (!Number.isInteger(userId) || !Number.isInteger(courseId)) {
            console.error('Invalid userId or courseId:', { userId, courseId });
            return false;
        }
        return true;
    };

    const handleAttemptTestResponse = async (attemptResponse, testNumber) => {
        if (attemptResponse.status === 200) {
            await tokenFetch(`${window._env_.API_BASE_URL}/wecp/test-link-request/`, {
                method: 'POST',
                body: JSON.stringify({
                    user_id: userId,
                    course_id: courseId,
                    test_number: testNumber,
                    test_link_sent: true,
                }),
            });
            setTestLinkRequested(prev => ({ ...prev, [testNumber]: true }));
            setTestLinkMessage(`A test link for attempt ${testNumber} will appear within 48 hours in your email.`);
            return true;
        } else if (attemptResponse.status === 404) {
            const data = await tokenFetch(`${window._env_.API_BASE_URL}/wecp/request-test-link/`, {
                method: 'POST',
                body: JSON.stringify({
                    user_id: userId,
                    course_id: courseId,
                    test_number: testNumber,
                    test_link_sent: false,
                }),
            });
            setTestLinkRequested(prev => ({ ...prev, [testNumber]: true }));
            setTestLinkMessage(`A test link for attempt ${testNumber} will appear within 48 hours in your email.`);
            return true;
        }
        throw new Error(`Attempt test failed with status ${attemptResponse.status}`);
    };

    const handleRequestTestLink = async (isReattempt = false) => {
        if (!validateUserAndCourse()) {
            setTestLinkMessage('Failed to request test link. User or course information is missing.');
            return false;
        }

        const testNumber = isReattempt ? 2 : 1;
        setIsRequesting(true);
        setTestLinkMessage('');
        setCertificateMessage('');

        try {
            const attemptResponse = await fetch(`${window._env_.API_BASE_URL}/wecp/attempt-test/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
                },
                body: JSON.stringify({
                    user_id: userId,
                    course_id: courseId,
                }),
            });

            return await handleAttemptTestResponse(attemptResponse, testNumber);
        } catch (error) {
            console.error('Error requesting test link:', error);
            setTestLinkMessage('Failed to request test link. Please try again.');
            return false;
        } finally {
            setIsRequesting(false);
        }
    };

    const handleCertificateSuccess = (scoreData) => {
        setCertificateMessage(`Congratulations! You passed the test with a score of ${scoreData.percentage}%. Certificate generated successfully. Please view in My Certificate section.`);
    };

    const handleCertificateFail = (scoreData) => {
        if (scoreData.attempt_number < 2) {
            setShowReattemptPrompt(true);
            setCertificateMessage(`You did not pass the test with a score of ${scoreData.percentage}%. Please choose whether to reattempt the test below.`);
        } else {
            setCertificateMessage(`You have failed the test with a score of ${scoreData.percentage}% and have no remaining attempts.`);
        }
    };

    const handleGenerateCertificate = async () => {
        if (!validateUserAndCourse()) {
            setCertificateMessage('Failed to generate certificate. User or course information is missing.');
            return;
        }

        setIsGeneratingCertificate(true);
        setCertificateMessage('');
        setTestLinkMessage('');
        setShowReattemptPrompt(false);
        setReattemptChoice(null);

        try {
            const scoreData = await tokenFetch(`${window._env_.API_BASE_URL}/wecp/candidate-score/${userId}/${courseId}`);

            if (scoreData.pass_status === 'Pass') {
                await tokenFetch(`${window._env_.API_BASE_URL}/certificates/generate`, {
                    method: 'POST',
                    body: JSON.stringify({
                        user_id: userId,
                        course_id: courseId,
                    }),
                });
                handleCertificateSuccess(scoreData);
            } else if (scoreData.pass_status === 'Fail') {
                handleCertificateFail(scoreData);
            }
        } catch (error) {
            console.error('Error generating certificate:', error);
            setCertificateMessage('Failed to generate certificate. Please try again.');
        } finally {
            setIsGeneratingCertificate(false);
        }
    };

    const handleReattemptSubmit = async () => {
        if (reattemptChoice === 'yes') {
            setCertificateMessage('');
            const success = await handleRequestTestLink(true);
            if (success) {
                setTestLinkMessage('A test link for attempt 2 will appear within 48 hours in your email.');
            }
        } else if (reattemptChoice === 'no') {
            setCertificateMessage('You have chosen not to reattempt the test.');
            setTestLinkMessage('');
        }
        setShowReattemptPrompt(false);
        setReattemptChoice(null);
    };

    if (!courseId) {
        return <div>Course not found</div>;
    }

    return (
        <div className="coursevideos-container">
            <div className="coursevideos-back-wrapper">
                <button className="coursevideos-back-btn" onClick={() => window.history.back()}>
                    ← <span>Back</span>
                </button>
            </div>
            <div className="coursevideos-header">
                <h1>{courseName}</h1>
            </div>
            <div className="coursevideos-main">
                <aside className="coursevideos-sidebar">
                    <h2>Video List{loadingProgress ? ' (Loading...)' : ` (${videoProgress.completed_videos}/${videoProgress.total_videos})`}</h2>
                    <VideoList
                        materials={materials}
                        completedMaterials={completedMaterials}
                        currentContent={currentContent}
                        onSelectContent={setCurrentContent}
                    />
                    <AssessmentItem
                        isCourseCompleted={isCourseCompleted()}
                        isActive={currentContent?.type === 'assessment'}
                        onAssessmentClick={handleAssessmentClick}
                    />
                </aside>
                <section className="coursevideos-player-section">
                    <div
                        className={`coursevideos-player-container ${currentContent && currentContent.type !== 'assessment' && completedMaterials[currentContent?.material_id] ? 'completed' : ''}`}
                        ref={playerContainerRef}
                        style={{ display: currentContent?.type === 'assessment' ? 'none' : 'block' }}
                    >
                        <div id="youtube-player" className="coursevideos-player" />
                        {currentContent && currentContent.type !== 'assessment' && (
                            <PlayerDetails currentContent={currentContent} completedMaterials={completedMaterials} />
                        )}
                    </div>
                    <PlayerSectionContent
                        currentContent={currentContent}
                        completedMaterials={completedMaterials}
                        testLinkRequested={testLinkRequested}
                        isRequesting={isRequesting}
                        onRequestTestLink={handleRequestTestLink}
                        testLinkMessage={testLinkMessage}
                        isGeneratingCertificate={isGeneratingCertificate}
                        onGenerateCertificate={handleGenerateCertificate}
                        certificateMessage={certificateMessage}
                        showReattemptPrompt={showReattemptPrompt}
                        reattemptChoice={reattemptChoice}
                        onReattemptChange={handleReattemptChange}
                        onReattemptSubmit={handleReattemptSubmit}
                    />
                </section>
            </div>
        </div>
    );
}

export default CourseVideos;